import { LinkBoqItemsToTaskAndForms as LinkBoqToTaskAndForm } from "@Features/forms";
import { linkBoqItems } from "../../../actions/schedules";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { modifyContractItem, linkEntitiesToContract } from "@Features/contracts/state/contracts.actions";
import ExistingTasks from "./ExistingTasks";
import { getContractCRUDPermission, getTotal } from "@Features/commercial-shared/contracts/contractHelpers";
import { useRef } from "react";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import { getSymbolForNumber } from "@Services/utils/helpers";
import { useTranslation, untranslated } from "@i18n";
import { Box } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Formatters, type Formatter, SlickDataView } from "slickgrid-react";
import SlickGrid, { typedColumns, type ColumnDefinition, type GridRowBase } from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TranslatedString } from "@i18n";
import type { GanttStatic } from "./dhtmlxgantt";
import type { GanttProps, ScheduleGridRef } from "../interface";
import type { AttachedItems, WarningState } from "@WebTypes/scheduleDomain";
import type { ComponentProps } from "react";
import type { LinkBoqItemsRequest, ModifyContractItemRequest } from "@ServerTypes/contracts/contracts.schema";

// Row shape of the linked-items tree grid. `"purchaseUnit.name"` is the dotted
// path SlickGrid reads at runtime, declared as an escape-hatch key alongside the
// real nested `purchaseUnit` object so `typedColumns` accepts the column `field`.
interface ContractTaskItemRow extends GridRowBase {
  serial?: number;
  code?: string;
  description?: string;
  revisedQuantity?: number;
  quantityLinkedManualy?: number;
  purchaseUnit?: { name?: string };
  "purchaseUnit.name"?: string;
  rate?: number;
  totalAmount?: number;
  linkedAmount?: number;
  itemId?: string;
  itemLevel?: number;
  numberingSystem?: string;
  children?: ContractTaskItemRow[];
  quantityInTask?: number;
  availableQuantity?: number;
  usedInForms?: number;
}

/** Buffered per-item quantity edit, keyed by BOQ item id, flushed by `handleSaveItemClick`. */
type TaskItemQuantityEdit = NonNullable<ModifyContractItemRequest["data"]>[number];

/**
 * Everything `indexNew` hands this hook. `contractDetails` and `changeOrderState` are re-used from
 * `GanttProps` rather than re-declared, so they stay in step with the chart's own props.
 */
interface UseGanttInContractsProps {
  gantt: GanttStatic;
  showBoqModal: boolean;
  setShowBoqModal: (value: boolean) => void;
  setRerenderGantt: (value: boolean) => void;
  contractDetails: GanttProps["contractDetails"];
  showWarning: WarningState;
  setShowWarning: (value: WarningState) => void;
  contractId?: ObjectIdString;
  setTaskModal: (value: boolean) => void;
  spaceId?: ObjectIdString;
  scheduleId?: ObjectIdString;
  projectId?: ObjectIdString;
  changeOrderState: GanttProps["changeOrderState"];
  attachedItems: AttachedItems;
  setAttachedItems: (value: AttachedItems) => void;
}

function useGanttInContracts(props: UseGanttInContractsProps) {
  const { t } = useTranslation();
  const {
    gantt,
    showBoqModal,
    setShowBoqModal,
    setRerenderGantt,
    contractDetails,
    showWarning,
    setShowWarning,
    contractId,
    setTaskModal,
    spaceId,
    scheduleId,
    projectId,
    changeOrderState,
    attachedItems,
    setAttachedItems,
  } = props;

  const taskItemQuantityRef = useRef<Record<string, TaskItemQuantityEdit>>({});
  const attachedItemRef = useRef<ScheduleGridRef<ContractTaskItemRow> | null>(null);

  const dispatch = useDispatch();

  const successCallback = () => {
    setRerenderGantt(true);
  };

  const addBoqItemsCallBack = async ({
    boqItems,
  }: Parameters<ComponentProps<typeof LinkBoqToTaskAndForm>["addBoqItemsCallBack"]>[0]) => {
    const selectedId = gantt.getSelectedId();
    const selectedTask = gantt.getTask(selectedId);
    const data = {
      originalTaskId: selectedTask.originalTaskId,
      boqItems,
      contractId: contractDetails!._id,
      action: "link" as const,
    };

    // SHAPE GAP: the picker's `LinkedBoqItem` carries grid-only fields and an optional quantity,
    // while /contracts/linkBoqItems declares an exact `{itemId, customQuantity?}` element. Erased
    // assertion — the extra fields are ignored server-side.
    const res = await linkBoqItems(data as LinkBoqItemsRequest);
    if (res.data?.status) {
      dispatch(
        showToastNotification({
          content: t("Items successfully linked with the task {{selectedTask_TaskName}}", {
            selectedTask_TaskName: selectedTask.TaskName,
          }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setShowBoqModal(false);
      successCallback();
    } else {
      dispatch(
        showToastNotification({
          content: t("{{res_data_errorMessage}}", { res_data_errorMessage: res.data.errorMessage }),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const linkBoqItemsModal = () => {
    const selectedId = gantt.getSelectedId();
    const selectedTask = gantt.getTask(selectedId);
    return (
      <LinkBoqToTaskAndForm
        // The child only reads `originalTaskId` off the task; DHTMLX's `Task` carries it through its
        // index signature, which is why the shapes have no declared overlap. Erased assertion.
        selectedTask={selectedTask as { originalTaskId?: string }}
        showBoqModal={showBoqModal}
        setShowBoqModal={setShowBoqModal}
        source="gantt"
        successCallback={successCallback}
        addBoqItemsCallBack={addBoqItemsCallBack}
      />
    );
  };

  const handleUnLinkTaskClick = async () => {
    const selectedId = gantt.getSelectedId();
    const selectedTask = gantt.getTask(selectedId);

    if (selectedTask.isPresentInLockedChildContract) {
      showToastNotification({
        content: t("Selected Task is already present in locked child contract and hence cannot be removed"),
        type: TOAST_TYPE_ERROR,
      });
    } else if (selectedTask.isAddedInForms) {
      showToastNotification({
        content: t("Selected Task is already added in a form and hence cannot be removed"),
        type: TOAST_TYPE_ERROR,
      });
    } else {
      // SHAPE GAP: /contracts/linkTaskToContract pins `entity` to a literal and requires 24-hex ids;
      // `contractId` is optional on this hook's props and the task id comes from DHTMLX. Erased
      // assertions.
      const data = {
        action: "un-link" as const,
        entity: "task" as const,
        contractId: contractId!,
        data: [selectedId as ObjectIdString],
      };
      const res = await linkEntitiesToContract(data);
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Activity Un-liniked successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        hideWarningModal();
        setRerenderGantt(true);
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_ERROR,
          }),
        );
        hideWarningModal();
      }
    }
  };

  const hideWarningModal = () => {
    setShowWarning({ status: false });
  };

  const warningModal = () => {
    const source = showWarning.source;
    let btnText;
    let contentTxt;
    let headerText;
    let callBackFun = async () => {};
    if (source == "unLinkTask") {
      btnText = "Un-Link";
      contentTxt = t(
        "Un-linking these activities will also remove all its childs from this and all child contracts. Are you sure you want to continue?",
      );
      headerText = t("Un-Link ?");
      callBackFun = handleUnLinkTaskClick;
    }
    return (
      <ModalStateDialogComponent
        footerContent={
          <div className="flex gap-4 items-center">
            <ButtonComponent onClick={callBackFun} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
              {btnText}
            </ButtonComponent>
            <ButtonComponent onClick={hideWarningModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        }
        width="30%"
        headerTitle={headerText}
        onCancel={hideWarningModal}
        isVisible={showWarning.status}
        modelContent={<div className="mt-6 mb-6">{contentTxt} </div>}
      />
    );
  };
  const refreshGrid = () => {
    setTaskModal(false);
  };

  // KNOWN i18n GAP, preserved verbatim: the messages reaching this helper are raw server strings and
  // one hard-coded English literal, so they are asserted rather than translated.
  const showErrorToast = (message: string) => {
    dispatch(
      showToastNotification({
        content: message as TranslatedString,
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  const showTaskContractsModal = () => {
    return (
      <ExistingTasks
        refreshGrid={refreshGrid}
        setTaskModal={setTaskModal}
        spaceId={spaceId}
        // Both are required by the tasks list; the contracts tab is only reachable with them set.
        scheduleId={scheduleId!}
        projectId={projectId!}
      />
    );
  };

  const handleSaveItemClick = async () => {
    const data: TaskItemQuantityEdit[] = [];
    Object.keys(taskItemQuantityRef.current).forEach((itemId: string) => {
      data.push(taskItemQuantityRef.current[itemId]!);
    });
    const res = await modifyContractItem({
      // The linked-items grid is only reachable from a contract-scoped schedule. Erased assertion.
      contractId: contractId!,
      contractVersionId: contractDetails!.versionId!,
      entity: "tasks",
      data,
    });
    if (res.data.status || data.length == 0) {
      setAttachedItems({ items: null });
      dispatch(
        showToastNotification({
          content: t("Quantity modified successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
  };
  const handleUnLinkItemClick = async () => {
    if (attachedItemRef.current!.dataView.getAllSelectedItems().length > 0) {
      const selectedItems = attachedItemRef.current!.dataView.getAllSelectedItems();
      const itemIds = selectedItems.map((item: ContractTaskItemRow) => item.itemId!);
      // SHAPE GAP: /contracts/linkBoqItems requires 24-hex ids for the task, the contract and every
      // BOQ item; all three arrive here from untyped grid/state values. Erased assertions.
      const data = {
        originalTaskId: attachedItems.originalTaskId as ObjectIdString,
        boqItems: itemIds as ObjectIdString[],
        contractId: contractId!,
        action: "un-link" as const,
      };
      const res = await linkBoqItems(data);
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Item removed successfully"),
          }),
        );
        setAttachedItems({ items: null });
        setRerenderGantt(true);
      } else {
        showErrorToast(res.data.errorMessage);
      }
    } else {
      showErrorToast("first select items to un-link");
    }
  };

  const handleQuantityChange = async (event: { target: { value: string } }, args: ContractTaskItemRow) => {
    const value = Number(event.target.value.replace(/,/g, ""));
    // SHAPE GAP: both ids are 24-hex on /contracts/modifyContractItem but reach here untyped, off the
    // grid row and the attached-items state. Erased assertions.
    taskItemQuantityRef.current[args.itemId!] = {
      customQuantity: value,
      originalTaskId: attachedItems.originalTaskId as ObjectIdString,
      itemId: args.itemId as ObjectIdString,
    };
  };

  const serialTemplate = (args: ContractTaskItemRow) => {
    const { serial, parentId, itemLevel, numberingSystem } = args;
    if (!serial) {
      return "";
    }
    const result = getSymbolForNumber(serial, numberingSystem, 0);

    // Every serial-bearing row carries its tree level. Erased assertion.
    const parentSerial = getSerialForParentInPreviewMode(parentId, itemLevel! - 1);

    return parentSerial ? `${parentSerial}.${result}` : `${result}`;
  };

  const linkedQuantityTemplate = (args: ContractTaskItemRow) => {
    if (args.hasChild) {
      return "";
    }
    const container = document.createElement("div");
    const root = createRoot(container);
    // Both are present on leaf rows, which is the only branch that reaches here. Erased assertions.
    const max = args.quantityInTask! + args.availableQuantity!;
    const min = args.usedInForms;
    root.render(
      <Box onClick={(e: React.SyntheticEvent) => e.stopPropagation()} className="flex justify-around items-center">
        {getContractCRUDPermission(contractDetails, changeOrderState) ? (
          <NumericTextBoxComponent
            placeholder={t("quantity")}
            value={args.quantityInTask}
            min={min}
            max={max}
            onBlur={(event) => handleQuantityChange(event, args)}
          />
        ) : (
          args.quantityInTask
        )}
      </Box>,
    );
    return container;
  };

  function totalAmountTemplate(args: ContractTaskItemRow) {
    if (args.hasChild) {
      const totalAmount = getTotal(args.children, "totalAmount");
      return Number(totalAmount.toFixed(2));
    }
    return args.totalAmount ? Number(args.totalAmount.toFixed(2)) : 0;
  }

  function linkedAmountTemplate(args: ContractTaskItemRow) {
    if (args.hasChild) {
      const totalAmount = getTotal(args.children, "linkedAmount");
      return Number(totalAmount.toFixed(2));
    }
    return args.linkedAmount ? Number(args.linkedAmount.toFixed(2)) : 0;
  }

  function getSerialForParentInPreviewMode(parentId: ContractTaskItemRow["parentId"], level: number) {
    if (level < 0) {
      return null;
    }
    // `SlickGrid.data` is the DataView, whose `items` is protected on the vendor class. Erased
    // assertion — this reads the full (unfiltered) tree, which the serial numbering depends on.
    const recordsAtCurrentLevel = (
      (
        attachedItemRef.current!.slickGrid as ScheduleGridRef<ContractTaskItemRow>["slickGrid"] & {
          data: SlickDataView<ContractTaskItemRow>;
        }
      ).data as SlickDataView<ContractTaskItemRow> & { items: ContractTaskItemRow[] }
    ).items.filter((record: ContractTaskItemRow) => record.itemLevel == level);
    const parentRecord = recordsAtCurrentLevel.find((record: ContractTaskItemRow) => record.itemId === parentId);
    if (!parentRecord) {
      return null;
    }
    const { parentId: grandParentId, serial, numberingSystem } = parentRecord;
    let result = getSymbolForNumber(serial, numberingSystem, 0);
    const grandParentSerial = getSerialForParentInPreviewMode(grandParentId, level - 1);
    if (grandParentSerial) {
      result = `${grandParentSerial}.${result}`;
    }
    return result;
  }

  const titleFormatter: Formatter<ContractTaskItemRow> = (_row, _cell, _value, _columnDef, dataContext) =>
    serialTemplate(dataContext);

  const treeColumns = useRef(
    typedColumns<ContractTaskItemRow>([
      {
        name: "S. No.",
        field: "serial",
        id: "serial",
        minWidth: 100,
        filterType: "Text",
        // slickgrid-react's built-in tree formatter, re-typed to this grid's row so it does not pin
        // `typedColumns`' row inference.
        formatter: Formatters.tree as NonNullable<ColumnDefinition<ContractTaskItemRow, "serial">["formatter"]>,
      },
      {
        name: "Code",
        field: "code",
        id: "code",
        minWidth: 100,
        filterType: "Text",
        formatter: (data: number, value: number, a: unknown, b: unknown, args: ContractTaskItemRow) => args.code ?? "",
      },
      {
        name: "Description",
        field: "description",
        minWidth: 120,
        id: "description",
        filterType: "Text",
      },
      {
        name: "Total Quantity",
        field: "revisedQuantity",
        id: "revisedQuantity",
        filterType: "Number",
      },
      {
        name: "Linked Quantity",
        field: "quantityLinkedManualy",
        id: "quantityLinkedManualy",
        filterType: "Number",
        minWidth: 150,
        formatter: (data: number, value: number, a: unknown, b: unknown, args: ContractTaskItemRow) =>
          linkedQuantityTemplate(args),
      },
      {
        name: "Unit",
        field: "purchaseUnit.name",
        id: "purchaseUnit.name",
        filterType: "Number",
        _valueGetter: (params: ContractTaskItemRow) => (params.hasChild ? "" : params.purchaseUnit?.name),
      },
      {
        name: "Rate",
        field: "rate",
        id: "rate",
        filterType: "Number",
      },
      {
        name: "Total Amount",
        field: "totalAmount",
        id: "totalAmount",
        filterType: "Number",
        formatter: (data: number, value: number, a: unknown, b: unknown, args: ContractTaskItemRow) =>
          totalAmountTemplate(args),
      },
      {
        name: "Linked Amount",
        field: "linkedAmount",
        id: "linkedAmount",
        filterType: "Number",
        formatter: (data: number, value: number, a: unknown, b: unknown, args: ContractTaskItemRow) =>
          linkedAmountTemplate(args),
      },
    ]),
  );

  const toolBarItems = [
    {
      name: t("ExpandAll"),
      prefixIcon: "Expand",
      id: "expandAll",
    },
    {
      name: t("CollapseAll"),
      prefixIcon: "Collapse",
      id: "collapseAll",
    },
  ];
  const toolbarClickHandler = async (args: string) => {
    if (args == "collapseAll") {
      attachedItemRef.current?.treeDataService.toggleTreeDataCollapse(true);
    } else if (args == "expandAll") {
      attachedItemRef.current?.treeDataService.toggleTreeDataCollapse(false);
    }
  };
  const getTaskItemsGrid = () => {
    return (
      <SlickGrid
        ref={attachedItemRef}
        columnDefinitions={treeColumns.current}
        enableWrapText
        treeGrid
        disableCustomSorting
        treeColIndex={1}
        titleFormatter={titleFormatter}
        gridOptions={{
          showHeaderRow: false,
          enableSorting: true,
        }}
        allowPagination={false}
        idMapping="itemId"
        hasChildMapping="hasChild"
        parentIdMapping="parentId"
        disableChildDataFetch
        initialSortColumnId="createdDate"
        toolbar={toolBarItems}
        // `AttachedItems.items` is still declared as a loose array on the shared type. Erased assertion.
        dataset={attachedItems.items as ContractTaskItemRow[]}
        enableFiltering={true}
        toolbarClickHandler={toolbarClickHandler}
      />
    );
  };

  const getTaskGridInContract = () => {
    return (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          getContractCRUDPermission(contractDetails, changeOrderState) && (
            <div className="w-full flex justify-between">
              <div className="flex gap-6">
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleSaveItemClick}>
                  <Text>{t("Save")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={() => setAttachedItems({ items: null })}
                >
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
              </div>
              <div>
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleUnLinkItemClick}>
                  <Text>{t("Un-Link")}</Text>
                </ButtonComponent>
              </div>
            </div>
          )
        }
        height="80%"
        width="80%"
        headerTitle={
          <div className="bg-button_light  w-full flex justify-between rounded-md">
            <div className="flex flex-col gap-1">
              <Text>{t("Linked Items")}</Text>
            </div>
          </div>
        }
        onCancel={() => setAttachedItems({ items: null })}
        isVisible
        modelContent={getTaskItemsGrid()}
      />
    );
  };

  return { linkBoqItemsModal, warningModal, showTaskContractsModal, getTaskGridInContract };
}
export default useGanttInContracts;
